import { FunctionComponent, useEffect } from "react";
import { Main } from "@App/Layouts/Main";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { BulletPoint } from "@App/Components/common/BulletPoint";
import { ContactUs } from "@App/Components/ContactUs";
import { CheckIcon } from "@App/Components/common/Icons";
import { useLocation } from "react-router-dom";
import { useScrollToTop } from "@App/hooks/useScrollToTop";
import { useRedirectBack } from "@App/hooks/useRedirectBack";
import { ContactForm } from "@App/Components/ContactForm";
import { OrderSummary } from "@App/Components/OrderSummary";
import { ServiceFAQs } from "@App/Components/ServiceFAQs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { State } from "@App/state";
import { FeedbackHint } from "@App/Components/Partials/FeedbackHint";
import BackButton from "../../Components/BackButton";
import { OrganizationIsALD } from "@App/utils/conditionals/organization";

export type ContactProps = {
  onSubmit: () => void;
  embedded?: boolean;
};

export const Contact: FunctionComponent<ContactProps> = ({
  embedded = false,
  onSubmit,
}: ContactProps) => {
  const { t } = useTranslation();
  useScrollToTop();
  const organization = useSelector((state: State) => state.orders.organization);

  // Redirect back to the start if parameters are missing.
  const { redirectOnMissingParams } = useRedirectBack();
  const location = useLocation();
  useEffect(() => {
    redirectOnMissingParams(
      ["garage", "service", "slot"],
      location.pathname.replace("/buchen", ""),
    );
  }, []);

  const submit = () => {
    onSubmit && onSubmit();
  };

  return (
    <div>
      <Main>
        <div className="grid md:grid-cols-2 gap-3 mt-10">
          <h1 className="order-1 text-3xl font-serif mb-3">{t("contactForm.headline")}</h1>
          <div className={"order-3"}>
            <BackButton />
            <div className="font-bold">{t("contactForm.sub-headline")}</div>
            <ContactForm onSuccessful={submit} />
            <div className={"mt-5"}>
              {t("contactForm.benefits.benefit-1") && (
                <BulletPoint text={t("contactForm.benefits.benefit-1")}>
                  <CheckIcon primary className={"text-primary w-5 h-5 shrink-0"} />
                </BulletPoint>
              )}
              {t("contactForm.benefits.benefit-2") && (
                <BulletPoint text={t("contactForm.benefits.benefit-2")}>
                  <CheckIcon primary className={"text-primary w-5 h-5 shrink-0"} />
                </BulletPoint>
              )}
            </div>
          </div>
          <OrderSummary />
        </div>
      </Main>
      {!embedded && (
        <section className="bg-neutral-100 pb-10">
          <TriangleDivider direction={"right"} background={"bg-white"} />
          <div className="container md:flex gap-10 mt-10">
            {!OrganizationIsALD(organization) && (
              <div className="w-full md:w-1/2">
                <ServiceFAQs />
              </div>
            )}
            <div className="w-full md:w-1/2">
              <ContactUs imageHidden={OrganizationIsALD(organization)} />
              {!OrganizationIsALD(organization) && (
                <div className={"mt-8"}>
                  <FeedbackHint />
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
